import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import ListPageHeader from "../../utilities/ListPageHeader";
import FinancialYearFilter from "./FinancialYearFilter";
import AgGridTable from "../../utilities/AgGridTable";
import FinancialYearForm from "./FinancialYearForm";
import { useNavigate } from "react-router-dom";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import { confirmAlert } from "react-confirm-alert";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, Tooltip } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import moment from "moment";

const url = `${process.env.REACT_APP_FINANCE_API_KEY_URL}${oAuthServiceConfig.financialYears}`;


const InventoryCheckboxRenderer = (props) => {
  return <Checkbox size="small" defaultChecked={props.data.availableInventory} sx={{ padding: 0 }} />;
};
const PricingCheckboxRenderer = (props) => {
  return <Checkbox size="small" defaultChecked={props.data.pricingAllowed} sx={{ padding: 0 }} />;
};
const RestCheckboxRenderer = (props) => {
  return <Checkbox size="small" defaultChecked={props.data.resetAllowed} sx={{ padding: 0 }} />;
};

const EditRenderer = (props) => {
  return (
    <span>
      <Tooltip title={"Edit"}>
        <IconButton size="small" onClick={() => props.handleEditRowDialogOpen(props.data)}>
          <FuseSvgIcon size={18} color="action">
            heroicons-outline:pencil
          </FuseSvgIcon>
        </IconButton>
      </Tooltip>
      <Tooltip title={"View"}>
        <IconButton size="small" onClick={() => props.handleViewRowDialogOpen(props.data)}>
          <FuseSvgIcon size={18} color="action">
            heroicons-outline:document-search
          </FuseSvgIcon>
        </IconButton>
      </Tooltip>
    </span>
  );
};

const FinancialYear = () => {
  const [open, setOpen] = useState(false);
  const [selectedRowToUpdate, setSelectedRowToUpdate] = useState({})
  const [rowData, setRowData] = useState([]);
  const [showPaginationDropdown, setShowPaginationDropdown] = useState(false);
  const [deleteDisable, setDeleteDisable] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();
  const financialYearUrl = `${process.env.REACT_APP_FINANCE_API_KEY_URL}${oAuthServiceConfig.financialYears}`;

  const PostingCheckboxRenderer = (props) => {
    return (
      props.data && <Checkbox
        size="small"
        onClick={(e) => {
          e.preventDefault();
          setSelectedRowToUpdate(props.data);
          setOpen(true);
        }}
        value={props?.data?.postingAllowed}
        defaultChecked={props?.data?.postingAllowed}
        sx={{ padding: 0 }}
      />
    );
  };

  const gridRef = useRef();
  const perPage = 10;
  const [gridApi, setGridApi] = useState(null);

  const handleDateFormat = (props) => {
    return props.value && moment(props.value).format('DD-MM-YYYY')
  }

  const [columnDefs, setColumnDefs] = useState([
    { field: "year", enableRowGroup: true, headerName: "Year", filter: "agNumberColumnFilter", },
    { field: "period", enableRowGroup: true, headerName: "Period", filter: "agNumberColumnFilter" },
    { field: "startDate", enableRowGroup: true, headerName: "Starting", cellRenderer: handleDateFormat },
    { field: "endDate", enableRowGroup: true, headerName: "Ending", cellRenderer: handleDateFormat },
    {
      field: "postingAllowed",
      headerName: "Posting Allowed",
      cellRenderer: PostingCheckboxRenderer,
    },
   
  ]);
  const [error, setError] = useState();
  const [successMsg, setSuccessMsg] = useState();
  const [snackState, setSnackState] = useState(false);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const loadData = async () => {
    try {
      const list = await OAuthService.getList(url + '?sort=year,desc&sort=period,desc&size=1000');
      let financialYears = list.data?._embedded?.financialYears;
      setRowData(financialYears);
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    if (gridApi) {
      loadData();
    }
  }, [gridApi]);

  const handleAddButton = () => {
    navigate('/add-financial-year')
  };

  const handleClose = () => {
    setOpen(false);
    
  };

  const onDelete = useCallback(
    (event) => {
      const selectedRow = [event.node.data];

      confirmAlert({
        title: "Confirm to delete",
        message: "Are you sure to delete.",
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              rowData.splice(event.node.rowIndex, 1);
              gridRef?.current?.api.applyTransaction({ remove: selectedRow });
              selectedRow.map(async (row) => {
                const id = row?._links?.self?.href.split("/").pop();
                try {
                  await OAuthService.deleteData(url, id);
                  setSuccessMsg("Data deleted Successfully");
                  setSnackState(true);
                  setError('')
                } catch (error) {
                  setError(error?.response?.data?.errorMessage || error.message)
                }
              });
            },
          },
          {
            label: "No",
            onClick: () => { },
          },
        ],
      });
    },
    [gridRef, rowData]
  );

  const handleSearchYear = useCallback((year) => {
    var countryFilterComponent = gridRef.current.api.getFilterInstance("year");
    countryFilterComponent.setModel({ values: [year] });
    gridRef.current.api.onFilterChanged();
  }, []);

  const handleClearYear = useCallback(() => {
    var countryFilterComponent = gridRef.current.api.getFilterInstance("year");
    countryFilterComponent.setModel(null);
    gridRef.current.api.onFilterChanged();
  }, []);

  const handleSearchPeriod = useCallback((period) => {
    var ageFilterComponent = gridRef.current.api.getFilterInstance("period");
    ageFilterComponent.setModel({
      // type: "lessThanOrEqual",
      type: "equal",
      filter: period,
    });
    gridRef.current.api.onFilterChanged();
  }, []);

  const handleSearch = useCallback((value) => {
    gridRef.current.api.setQuickFilter(value);
  }, []);

  const onBtExport = useCallback(() => {
    const params = {
      fileName: 'FinancialYear.xlsx',
      sheetName: 'Financial Year'
    };
    gridRef.current.api.exportDataAsExcel(params);
  }, []);

  const onRowDoubleClicked = (params) => {
    if (selectedRows[0]) {
      navigate('/edit-financial-year/' + selectedRows[0]?.id)
    }
  };

  const onSelectionChanged = (params) => {
    setSelectedRows(params.api.getSelectedRows());
  };

  const isRowSelectable = useMemo(() => {
    return (rowNode) => {
      return rowNode.data ? true : false;
    };
  }, []);

  const handlePurge = () => {
    selectedRows.map(async (row) => {
      const id = row?._links?.self?.href.split('/').pop();
      try {
        await OAuthService.deleteData(url, id);
        setError('')
        setSuccessMsg('Data deleted Successfully');
        setSnackState(true);
        loadData();
      } catch (error) {
        setError(error?.response?.data?.errorMessage || error.message);
      }
    });
  };

  const updateData = async () => {
    const data={
      ...selectedRowToUpdate,
      postingAllowed: !selectedRowToUpdate.postingAllowed
    }
    await OAuthService.updateData(financialYearUrl, selectedRowToUpdate.id, data);
    handleClose()
    setSuccessMsg("Data updated Successfully");
    setSnackState(true);
    loadData();
  }

  useEffect(() => {
    if (selectedRows.length > 0) {
      setDeleteDisable(false);
    } else {
      setDeleteDisable(true);
    }
  }, [selectedRows]);

  return (
    <>
      <ListPageHeader
        componentName="Financial Year"
        onBtExport={onBtExport}
        handlePurge={handlePurge}
        handleAddButton={handleAddButton}
        pageName="Financial Year"
        //headerInputFields={exchangeRateFilter()}
        error={error}
        deleteDisable={deleteDisable}
        successMsg={successMsg}
        gridRef={gridRef}
        snackState={snackState}
        showPaginationDropdown={showPaginationDropdown}
      />
      {/* <FinancialYearFilter
        handleSearchYear={handleSearchYear}
        handleSearchPeriod={handleSearchPeriod}
        handleMainRowDialogOpen={handleAddButton}
        handleClearYear={handleClearYear}
        handleSearch={handleSearch}
      /> */}
      <div className="h-full pr-24 pl-24 w-full">
        <AgGridTable
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          gridRef={gridRef}
          rowData={rowData}
          // getContextMenuItems={getContextMenuItems}
          editable={false}
          pagination={false}
          isRowSelectable={isRowSelectable}
          onSelectionChanged={onSelectionChanged}
          onRowDoubleClicked={onRowDoubleClicked}
        />
      </div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to update?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={updateData}>Yes</Button>
          <Button onClick={handleClose} autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinancialYear;
